import { useCallback, useEffect, useRef, useState } from "react";
import { translateTexts } from "../services/api";
import { translatePartDescription as localTranslate } from "../utils/partDescriptions";

// Module-level cache shared across all component instances so a description is
// only ever translated once per session (the backend also caches in S3).
// Key: original German text, Value: translated English text.
const translationCache = new Map();

/**
 * Translate a set of German part descriptions to English via AWS Translate,
 * with local-dictionary fallback for instant rendering.
 *
 * @param {string[]} texts   Descriptions currently visible (e.g. current page).
 * @param {string}   language Active UI language ("EN", "DE", "SE").
 * @returns {(text: string) => string} A synchronous translator for rendering.
 */
export function usePartTranslations(texts, language) {
	// Bump this to re-render when new translations arrive from the backend.
	const [, setVersion] = useState(0);
	const pendingRef = useRef(new Set());

	useEffect(() => {
		if (language !== "EN" || !Array.isArray(texts) || texts.length === 0) {
			return;
		}

		// Collect uncached, not-in-flight, unique descriptions.
		const seen = new Set();
		const missing = [];
		for (const raw of texts) {
			if (!raw || typeof raw !== "string") continue;
			const key = raw.trim();
			if (!key || key === "-") continue;
			if (translationCache.has(key)) continue;
			if (pendingRef.current.has(key)) continue;
			if (seen.has(key)) continue;
			seen.add(key);
			missing.push(key);
		}

		if (missing.length === 0) return;

		for (const key of missing) pendingRef.current.add(key);

		let cancelled = false;
		translateTexts(missing, "en", "de")
			.then((map) => {
				if (cancelled) return;
				for (const original of missing) {
					// Fall back to original if the backend couldn't translate it.
					translationCache.set(original, map[original] ?? original);
					pendingRef.current.delete(original);
				}
				setVersion((v) => v + 1);
			})
			.catch(() => {
				// On failure, drop the in-flight markers so a later attempt can retry.
				for (const key of missing) pendingRef.current.delete(key);
			});

		return () => {
			cancelled = true;
		};
	}, [texts, language]);

	return useCallback(
		(text) => {
			if (language !== "EN" || !text || text === "-") return text;
			const key = typeof text === "string" ? text.trim() : text;
			if (translationCache.has(key)) return translationCache.get(key);
			// Instant local-dictionary fallback while the API result loads.
			return localTranslate(text, language);
		},
		[language],
	);
}

export default usePartTranslations;
